import * as React from 'react';
import { Filter } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { useTailoredSupplyAppsQuery } from '../../hooks';
import {
  TailoredSupplySortDirection,
  TailoredSupplySortProperty,
  tailoredSupplyPageSizes,
  type TailoredSupplyApp,
  type TailoredSupplyAppsFilter,
} from '../../model';
import { TailoredSupplyAppsFilters } from './TailoredSupplyAppsFilters';

interface TailoredSupplyAppsTableProps {
  filter: TailoredSupplyAppsFilter;
  selectedIds: ReadonlySet<string>;
  onFilterChange: (filter: TailoredSupplyAppsFilter) => void;
  onSelectionChange: (items: TailoredSupplyApp[], selected: boolean) => void;
}

export function TailoredSupplyAppsTable({
  filter,
  selectedIds,
  onFilterChange,
  onSelectionChange,
}: TailoredSupplyAppsTableProps) {
  const query = useTailoredSupplyAppsQuery(filter);
  const items = React.useMemo(() => query.data?.content ?? [], [query.data?.content]);
  const selectedOnPage = items.filter((item) => selectedIds.has(item.id)).length;
  const allOnPageSelected = items.length > 0 && selectedOnPage === items.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;
  const [filtersOpen, setFiltersOpen] = React.useState(() => hasAppsFilters(filter));
  const search = useDebouncedValue(
    filter.searchTerm,
    (searchTerm) => {
      onFilterChange({ ...filter, searchTerm, page: 0 });
    },
    650,
  );

  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<TailoredSupplyApp>([
        {
          kind: DashboardDataTableColumnKind.Selection,
          id: 'selection',
          headerChecked: someOnPageSelected ? 'indeterminate' : allOnPageSelected,
          headerLabel: dashboardT('tailoredSupply.selectAllApps'),
          onHeaderChange: (checked) => {
            onSelectionChange(items, checked);
          },
          checked: (app) => selectedIds.has(app.id),
          label: (app) => dashboardT('tailoredSupply.selectApp', { name: app.name }),
          onChange: (app, checked) => {
            onSelectionChange([app], checked);
          },
        },
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'name',
          header: dashboardT('tailoredSupply.name'),
          headerClassName: 'min-w-64 pl-4',
          className: 'min-w-64 pl-4 font-medium',
          sortable: true,
          sortKey: TailoredSupplySortProperty.Name,
          value: (app) => app.name,
        },
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'publisher',
          header: dashboardT('tailoredSupply.publisher'),
          headerClassName: 'min-w-64',
          className: 'min-w-64',
          value: (app) => app.publisherNames.join(', '),
        },
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'bundleId',
          header: dashboardT('tailoredSupply.bundleId'),
          headerClassName: 'w-56',
          className: 'w-56',
          value: (app) => app.bundleId,
        },
        {
          kind: DashboardDataTableColumnKind.Text,
          id: 'platform',
          header: dashboardT('tailoredSupply.platform'),
          headerClassName: 'w-40',
          className: 'w-40',
          value: (app) => app.platform?.name,
        },
      ]),
    [allOnPageSelected, items, onSelectionChange, selectedIds, someOnPageSelected],
  );

  return (
    <DashboardDataTable
      title={dashboardT('tailoredSupply.apps')}
      columns={columns}
      items={items}
      getRowKey={(app) => app.id}
      minWidthClassName="min-w-[980px]"
      loading={query.isLoading}
      loadingMessage={dashboardT('tailoredSupply.appsLoading')}
      errorMessage={query.isError ? dashboardT('tailoredSupply.appsLoadError') : undefined}
      emptyMessage={dashboardT('tailoredSupply.appsEmpty')}
      toolbar={
        <div className="flex w-full min-w-0 items-center gap-2">
          <DashboardTableSearchInput
            className="min-w-0 flex-1"
            aria-label={dashboardT('tailoredSupply.appsSearch')}
            value={search.draft}
            onChange={(event) => {
              search.setDraft(event.target.value);
            }}
          />
          <Button
            type="button"
            variant={filtersOpen ? 'secondary' : 'outline'}
            size="icon"
            aria-label={dashboardT(
              filtersOpen ? 'tailoredSupply.hideFilters' : 'tailoredSupply.showFilters',
            )}
            aria-pressed={filtersOpen}
            onClick={() => {
              setFiltersOpen((open) => !open);
            }}
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>
      }
      filterToolbar={
        filtersOpen ? (
          <TailoredSupplyAppsFilters filter={filter} onChange={onFilterChange} />
        ) : undefined
      }
      pagination={{
        value: {
          page: filter.page,
          pageSize: filter.size,
          totalItems: query.data?.totalElements ?? 0,
        },
        pageSizes: tailoredSupplyPageSizes,
        disabled: query.isFetching,
        onChange: ({ page, pageSize }) => {
          onFilterChange({
            ...filter,
            page: pageSize === filter.size ? page : 0,
            size: pageSize,
          });
        },
      }}
      sort={{
        value: filter.sortProperty
          ? {
              key: filter.sortProperty,
              direction:
                filter.sortDirection === TailoredSupplySortDirection.Ascending
                  ? DashboardDataTableSortDirection.Asc
                  : DashboardDataTableSortDirection.Desc,
            }
          : undefined,
        onChange: (next) => {
          if (!next) {
            onFilterChange({ ...filter, sortProperty: null, page: 0 });
            return;
          }
          onFilterChange({
            ...filter,
            page: 0,
            sortProperty: next.key as TailoredSupplySortProperty,
            sortDirection:
              next.direction === DashboardDataTableSortDirection.Asc
                ? TailoredSupplySortDirection.Ascending
                : TailoredSupplySortDirection.Descending,
          });
        },
      }}
    />
  );
}

function hasAppsFilters(filter: TailoredSupplyAppsFilter) {
  return (
    filter.publisherIds.length > 0 ||
    filter.platformIds.length > 0 ||
    filter.languages.length > 0 ||
    filter.iabCategoryIds.length > 0
  );
}
